import {
  ADDRESS_PARAMS,
  ADDRESS_SEARCH_PARAMS,
  CATEGORIES_PARAMS,
  PLAN_PARAMS,
  PROVIDER_NAME_PARAMS,
  SPECIALTY_PARAMS,
  SUB_SPECIALTY_PARAMS,
  GENDER_PARAMS,
  APPOINTMENT_TYPE_PARAMS
} from './useInitFilters.constants'
import {
  AccreditedGender,
  AppointmentType,
  FilterItem,
  GetFilterResponse,
  establishmentCategories,
  EstablishmentCategory
} from '@/modules/accredited-establishments/api/types'
import {
  toAccreditedGender,
  toAppointmentType
} from '@/modules/accredited-establishments/functions/parse-enum-param'

interface GetValidParamsProps {
  searchParams: URLSearchParams
  specialties: GetFilterResponse
  plans: GetFilterResponse
  addresses?: GetFilterResponse
  /* Options of the specialty in the url; fetched only when the param is set. */
  subSpecialties?: GetFilterResponse
}

interface ClearInvalidParamsProps {
  searchParams: URLSearchParams
  validCategories: EstablishmentCategory[]
  validSpecialty?: FilterItem
  validSubSpecialty?: FilterItem
  validGender?: AccreditedGender
  validAppointmentType?: AppointmentType
  validPlan?: FilterItem
  validAddress?: FilterItem
  validProviderName?: string
}

const getValidParams = ({
  searchParams,
  specialties,
  plans,
  addresses,
  subSpecialties
}: GetValidParamsProps) => {
  // Extract URL parameters
  const categoryIds = searchParams.get(CATEGORIES_PARAMS)?.split(',')
  const specialtyId = searchParams.get(SPECIALTY_PARAMS)
  const subSpecialtyId = searchParams.get(SUB_SPECIALTY_PARAMS)
  const genderParam = searchParams.get(GENDER_PARAMS)
  const appointmentTypeParam = searchParams.get(APPOINTMENT_TYPE_PARAMS)
  const planId = searchParams.get(PLAN_PARAMS)
  const addressId = searchParams.get(ADDRESS_PARAMS)
  const providerName = searchParams.get(PROVIDER_NAME_PARAMS) ?? undefined

  // Get default "ALL" options
  const defaultSpecialty = specialties?.items.find(
    specialty => specialty.value === 'ALL'
  )
  const defaultPlan = plans?.items.find(plan => plan.value === 'ALL')

  // Filter valid categories from URL params
  const validCategories = establishmentCategories.filter(
    category => categoryIds?.includes(category)
  )

  // Find selected specialty, plan and address from loaded options
  const selectedPlanFromParams = plans.items.find(plan => plan.value === planId)

  const selectedSpecialtyFromParams = specialties.items.find(
    specialty => specialty.value === specialtyId
  )

  const selectedAddress = addresses?.items.find(
    address => address.value === addressId
  )

  // Only show specialty if SPECIALIST category is selected
  const selectedSpecialty = validCategories.includes('SPECIALIST')
    ? selectedSpecialtyFromParams || defaultSpecialty
    : undefined

  // Use selected plan or fallback to default
  const selectedPlan = selectedPlanFromParams || defaultPlan

  /*
   * The subspecialty hangs off the specialty that came from the url, never off
   * the ALL fallback: an id resolved against another specialty's options would
   * filter the search by something the user never chose.
   */
  const selectedSubSpecialty =
    selectedSpecialty && selectedSpecialtyFromParams
      ? subSpecialties?.items.find(
          subSpecialty => subSpecialty.value === subSpecialtyId
        )
      : undefined

  /*
   * Genero e atendimento nao dependem da especialidade, mas sao chips da
   * categoria de especialista: fora dela nao ha onde exibi-los nem o que
   * filtrar.
   */
  const isSpecialistSearch = validCategories.includes('SPECIALIST')

  const selectedGender = isSpecialistSearch
    ? toAccreditedGender(genderParam)
    : undefined

  const selectedAppointmentType = isSpecialistSearch
    ? toAppointmentType(appointmentTypeParam)
    : undefined

  return {
    filters: {
      categories: validCategories,
      plan: selectedPlan,
      specialty: selectedSpecialty,
      subSpecialty: selectedSubSpecialty,
      gender: selectedGender,
      appointmentType: selectedAppointmentType,
      address: selectedAddress,
      providerName
    },
    params: {
      categories: validCategories,
      plan: selectedPlanFromParams,
      specialty: selectedSpecialtyFromParams,
      subSpecialty: selectedSubSpecialty,
      gender: selectedGender,
      appointmentType: selectedAppointmentType,
      address: selectedAddress,
      providerName
    }
  }
}

const clearInvalidParams = ({
  searchParams,
  validCategories,
  validSpecialty,
  validSubSpecialty,
  validGender,
  validAppointmentType,
  validPlan,
  validAddress,
  validProviderName
}: ClearInvalidParamsProps) => {
  const keysToRemove: string[] = []

  /*
   * A key only needs clearing when the url actually carried it. Without the
   * `searchParams.has` guard, a perfectly clean url (nothing in it at all)
   * would still "resolve to invalid" on every field and get written back —
   * harmless with the old raw history.replaceState, but with router.replace
   * every one of those writes is a real navigation, so a bare url re-fires
   * this effect, which writes again, forever.
   */
  if (!validCategories.length && searchParams.has(CATEGORIES_PARAMS)) {
    keysToRemove.push(CATEGORIES_PARAMS)
  }

  if (!validSpecialty && searchParams.has(SPECIALTY_PARAMS)) {
    keysToRemove.push(SPECIALTY_PARAMS)
  }

  if (!validSubSpecialty && searchParams.has(SUB_SPECIALTY_PARAMS)) {
    keysToRemove.push(SUB_SPECIALTY_PARAMS)
  }

  if (!validGender && searchParams.has(GENDER_PARAMS)) {
    keysToRemove.push(GENDER_PARAMS)
  }

  if (!validAppointmentType && searchParams.has(APPOINTMENT_TYPE_PARAMS)) {
    keysToRemove.push(APPOINTMENT_TYPE_PARAMS)
  }

  if (!validPlan && searchParams.has(PLAN_PARAMS)) {
    keysToRemove.push(PLAN_PARAMS)
  }

  if (
    !validAddress &&
    (searchParams.has(ADDRESS_PARAMS) ||
      searchParams.has(ADDRESS_SEARCH_PARAMS))
  ) {
    keysToRemove.push(ADDRESS_PARAMS)
    keysToRemove.push(ADDRESS_SEARCH_PARAMS)
  }

  if (!validProviderName && searchParams.has(PROVIDER_NAME_PARAMS)) {
    keysToRemove.push(PROVIDER_NAME_PARAMS)
  }

  return keysToRemove
}

export { getValidParams, clearInvalidParams }
